import { HotelInfoModel } from '@/__generated';
import HotelBookmarkIcon from '@/components/App/Hotels/HotelBookmarkIcon';
import { HOTEL_CONFIG } from '@/config';
import { useGetBookmarkHotelCodes } from '@/hooks/dataProvider';
import { DataSourceArray } from 'photoswipe';
import React from 'react';
import { Gallery, GalleryProps, Item } from 'react-photoswipe-gallery';
import { Swiper, SwiperSlide } from 'swiper/react';
import ImageGallery from 'react-image-gallery';

type Props = {
  hotelInfo: HotelInfoModel;
};

const GalleryItem = ({ url }: { url: string }) => {
  return (
    <div className="w-100 h-100 d-flex items-center justify-center">
      <img src={url} alt="hotel" style={{ maxHeight: '80%', maxWidth: '80%' }} />
    </div>
  );
};
const MAX_WIDTH_DISPLAY_SLIDE = 1000;

const uiElements: GalleryProps['uiElements'] = [
  {
    name: 'bulletsIndicator',
    order: 9,
    isButton: false,
    appendTo: 'wrapper',
    onInit: (el, pswpInstance) => {
      let prevIndex = -1;
      const thumbnails: HTMLElement[] = [];

      /* eslint-disable no-param-reassign */
      el.style.position = 'absolute';
      el.style.bottom = '20px';
      el.style.left = '10px';
      el.style.right = '0';
      el.style.display = 'grid';
      el.style.gridGap = '10px';
      el.style.gridTemplateColumns = 'repeat(auto-fit, 40px)';
      el.style.gridTemplateRows = 'repeat(auto-fit, 40px)';
      el.style.justifyContent = 'center';
      /* eslint-enable no-param-reassign */

      const images = (pswpInstance.options.dataSource as any)?.map(x => x.content.props.url) as Array<string>;

      for (let i = 0; i < images.length; i++) {
        const img = images[i];

        const thumbnail = document.createElement('div');
        thumbnail.style.transition = 'transform 0.15s ease-in';
        thumbnail.style.opacity = '0.6';
        thumbnail.style.cursor = 'pointer';
        thumbnail.onclick = (e: MouseEvent) => {
          const target = e.target as HTMLImageElement | HTMLDivElement;
          const thumbnailEl = target.tagName === 'IMG' ? target.parentElement : (e.target as HTMLImageElement | HTMLDivElement);
          if (thumbnailEl) {
            pswpInstance.goTo(thumbnails.indexOf(thumbnailEl));
          }
        };

        const thumbnailImage = document.createElement('img');
        thumbnailImage.setAttribute('src', img || '');
        thumbnailImage.style.width = '100%';
        thumbnailImage.style.height = '100%';
        thumbnailImage.style.objectFit = 'cover';

        thumbnail.appendChild(thumbnailImage);

        el.appendChild(thumbnail);

        thumbnails.push(thumbnail);
      }

      pswpInstance.on('change', () => {
        if (prevIndex >= 0) {
          const prevThumbnail = thumbnails[prevIndex];
          prevThumbnail.style.opacity = '0.6';
          prevThumbnail.style.cursor = 'pointer';
          prevThumbnail.style.transform = 'scale(1)';
        }

        const currentThumbnail = thumbnails[pswpInstance.currIndex];
        currentThumbnail.style.opacity = '1';
        currentThumbnail.style.cursor = 'unset';
        currentThumbnail.style.transform = 'scale(1.2)';

        prevIndex = pswpInstance.currIndex;
      });
    },
  },
];

export default function HotelDetailGallery({ hotelInfo }: Props) {
  const { data: bookmarkHotelCodes } = useGetBookmarkHotelCodes();
  let images = hotelInfo.images || [];
  if (!images.length) {
    return null;
  }
  if (window.innerWidth < MAX_WIDTH_DISPLAY_SLIDE || images.length < 5) {
    return <ImageGallery lazyLoad items={hotelInfo?.images?.map(x => ({ thumbnail: x.url || '', original: x.url || '' })) || []} />;
  } else {
    return (
      <Gallery uiElements={uiElements}>
        <div className="galleryGrid -type-1 mb-20">
          {images.map((img, index) => {
            if (index === 0) {
              return (
                <div className="galleryGrid__item relative d-flex" key={index}>
                  <Item content={<GalleryItem url={img.url || ''} />}>
                    {({ ref, open }) => (
                      <img src={img.url || ''} ref={ref as any} onClick={open} alt={img.url || 'hotel'} role="button" className="rounded-4" />
                    )}
                  </Item>
                  <div className="absolute px-20 py-20 col-12 d-flex justify-end">
                    <HotelBookmarkIcon size={40} bookmarkHotelCodes={bookmarkHotelCodes} hotelCode={hotelInfo.code || ''} />
                  </div>
                </div>
              );
            }
            return (
              <div className="galleryGrid__item" key={index} style={index >= 5 ? { display: 'none' } : undefined}>
                <Item content={<GalleryItem url={img.url || ''} />}>
                  {({ ref, open }) => (
                    <img ref={ref as any} onClick={open} src={img.url || ''} alt={img.url || 'hotel'} className="rounded-4" role="button" />
                  )}
                </Item>
              </div>
            );
          })}

          {/* End .galleryGrid__item */}
        </div>
      </Gallery>
    );
  }
}
